import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Row,
  Radio,
  Select,
  Space,
  Modal,
  Spin,
  Table,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  DownloadOutlined,
  InfoCircleFilled,
  MinusSquareFilled,
  PlusSquareFilled,
  UploadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { t } from "i18next";
import * as XLSX from "xlsx";
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
} from "../../axios";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import moment from "moment";

const GENERAL_PROCUREMENT_RFQ_CREATE_URL = "general-rfqs/";

/** Coerce API message / DRF-style validation payloads into a string for AntD notifications. */
function formatNotificationMessage(value, fallback = "") {
  if (value == null || value === "") {
    return fallback || "Something went wrong.";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => formatNotificationMessage(v, ""))
      .filter((s) => s && String(s).trim());
    return parts.length ? parts.join("; ") : fallback || "Something went wrong.";
  }
  if (typeof value === "object") {
    const parts = [];
    for (const [key, val] of Object.entries(value)) {
      const inner = formatNotificationMessage(val, "");
      if (inner && String(inner).trim()) {
        parts.push(`${key.replace(/_/g, " ")}: ${inner}`);
      }
    }
    if (parts.length) return parts.join(" ");
    try {
      return JSON.stringify(value);
    } catch {
      return fallback || "Something went wrong.";
    }
  }
  return fallback || "Something went wrong.";
}

const EMPTY_ROW = {
  itemDescription: "",
  qty: "",
  uom: "",
  unitRate: "",
  gstRate: "",
  remarks: "",
  vendor: [],
};

function normalizeVendorIds(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.filter((v) => v != null && v !== "");
  }
  return [value];
}

function vendorIdsSignature(vendorIds) {
  return normalizeVendorIds(vendorIds)
    .map(String)
    .sort()
    .join(",");
}

function extractVendorIdsFromItem(it) {
  const generalVendors = it?.general_vendors ?? [];
  if (generalVendors.length > 0) {
    const selected = generalVendors.filter((v) => v.is_selected_vendor);
    const list = selected.length ? selected : generalVendors;
    return list.map((v) => v.id).filter((id) => id != null && id !== "");
  }
  return normalizeVendorIds(it.vendor ?? it.vendor_id);
}

function appendVendorId(existingIds, newId) {
  const ids = normalizeVendorIds(existingIds);
  if (newId == null || newId === "") return ids;
  if (ids.some((id) => String(id) === String(newId))) return ids;
  return [...ids, newId];
}

const TEMPLATE_HEADERS = [
  "Item Description",
  "Qty",
  "UoM",
  "Unit Rate",
  "GST Rate",
  "Remarks from Buyer (if any)",
];

const CURRENCY_OPTIONS = [{ value: "INR", label: "Rupee ₹ (INR)" }];
const PRICE_BASIS_OPTIONS = [
  { value: "FIRM", label: "Firm" },
  { value: "VARIABLE", label: "Variable" },
];
const SCOPE_OPTIONS = [
  { value: "Vendor Scope", label: "Vendor Scope" },
  { value: "Buyer Scope", label: "Buyer Scope" },
];

function downloadTemplate(kind) {
  const sheetName = kind === "quick" ? "Quick_RFQ" : "General_RFQ";
  const wsData = [
    TEMPLATE_HEADERS,
    [
      "Example: 12x3 - Cu",
      10,
      "Meter",
      120,
      5,
      "Optional remarks",
    ],
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const fileName =
    kind === "quick"
      ? "Quick_RFQ_General_Procurement_Template.xlsx"
      : "General_RFQ_General_Procurement_Template.xlsx";
  XLSX.writeFile(wb, fileName);
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function limitToDigits(value, maxDigits, allowDecimal = true) {
  if (value == null) return "";
  let v = String(value);
  v = allowDecimal
    ? v
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/^\./, "0.")
    : v.replace(/[^0-9]/g, "");

  const parts = v.split(".");
  const intPart = (parts[0] || "").replace(/^0+(?=\d)/, "0");
  const decPart = parts[1] || "";

  // enforce digit budget across int + decimal parts (ignore dot)
  const digits = (intPart + decPart).replace(/\D/g, "");
  if (digits.length <= maxDigits) return v;

  const trimmedDigits = digits.slice(0, maxDigits);
  // rebuild keeping original decimal placement if any; otherwise just return trimmed digits
  if (!allowDecimal || parts.length === 1) return trimmedDigits;

  // allocate digits back to int/dec based on original int length (at least 1 digit)
  const desiredIntLen = Math.max(1, Math.min(intPart.replace(/\D/g, "").length, trimmedDigits.length));
  const newInt = trimmedDigits.slice(0, desiredIntLen);
  const newDec = trimmedDigits.slice(desiredIntLen);
  return newDec.length ? `${newInt}.${newDec}` : newInt;
}

function rowIsEffectivelyEmpty(r) {
  return (
    !String(r.itemDescription ?? "").trim() &&
    !String(r.qty ?? "").trim() &&
    !String(r.uom ?? "").trim() &&
    !String(r.unitRate ?? "").trim() &&
    !String(r.gstRate ?? "").trim() &&
    !String(r.remarks ?? "").trim()
  );
}

function parseMomentLoose(val) {
  if (val == null || val === "") return null;
  if (moment.isMoment(val)) return val;
  const m = moment(val);
  return m.isValid() ? m : null;
}

function normalizePriceBasis(v) {
  const u = String(v ?? "").trim().toUpperCase();
  if (u === "FIRM" || u === "VARIABLE") return u;
  const lower = String(v ?? "").toLowerCase();
  if (lower.includes("variable")) return "VARIABLE";
  return "FIRM";
}

/** Map GET `general-rfqs/:id/` or view-detail response into local form state */
export function mapGeneralRfqApiToForm(detail) {
  if (!detail || typeof detail !== "object") return null;

  const rawItems = detail.items ?? detail.rfq_items ?? [];
  const rowsFromApi = rawItems.map((it) => {
    const qtyRaw =
      it.quantity != null && it.quantity !== "" ? String(it.quantity) : "";
    const rateRaw =
      it.unit_rate != null && it.unit_rate !== "" ? String(it.unit_rate) : "";
    const qty = qtyRaw
      ? limitToDigits(qtyRaw.replace(/[^0-9.]/g, ""), 6, true)
      : "";
    const unitRate = rateRaw
      ? limitToDigits(rateRaw.replace(/[^0-9.]/g, ""), 6, true)
      : "";
    const gstRaw =
      it.gst_rate != null && it.gst_rate !== "" ? String(it.gst_rate) : "";
    const gstRate = gstRaw
      ? limitToDigits(gstRaw.replace(/[^0-9.]/g, ""), 5, true)
      : "";

    return {
      id: it.id ?? null,
      itemDescription: String(
        it.item_description ?? it.itemDescription ?? "",
      ).trim(),
      qty,
      uom: String(it.unit_of_measurement ?? it.uom ?? "").trim(),
      unitRate,
      gstRate,
      remarks: String(
        it.remarks_from_buyer ?? it.remarks ?? "",
      ).trim(),
      vendor: extractVendorIdsFromItem(it),
      vendorName:
        it.vendor_name ??
        it.vendor_registered_name ??
        undefined,
    };
  });

  const rows =
    rowsFromApi.length > 0 ? rowsFromApi : [{ ...EMPTY_ROW }];

  const rfqDetails = {
    currency: detail.currency ?? "INR",
    price_basis: normalizePriceBasis(detail.price_basis),
    freight: detail.freight ?? "Vendor Scope",
    transit_insurance:
      detail.transit_insurance ?? detail.transport_insurance ?? "Vendor Scope",
    unloading: detail.unloading ?? "Vendor Scope",
    buyer_scope: detail.buyer_scope ?? "Buyer Scope",
    rfq_bid_due_date_time:
      parseMomentLoose(detail.rfq_bid_due_date_time ?? detail.rfq_due_date) ??
      moment().add(2, "days").set({ hour: 16, minute: 0 }),
    delivery_date:
      parseMomentLoose(detail.delivery_date) ??
      moment().add(7, "days"),
  };

  const templateKind =
    detail.rfq_type === "quick" || detail.rfq_type === "Quick"
      ? "quick"
      : "general";

  let vendorMode = "common";
  let commonVendorIds = normalizeVendorIds(
    detail.vendor ?? detail.vendor_id ?? detail.vendors,
  );

  const vm = String(detail.vendor_mode ?? "").toLowerCase().replace(/-/g, "");
  if (vm === "peritem") {
    vendorMode = "perItem";
    commonVendorIds = [];
  } else if (vm === "common") {
    vendorMode = "common";
  } else {
    const vendorSetsFromRows = rows
      .map((r) => vendorIdsSignature(r.vendor))
      .filter(Boolean);
    const uniqueSets = [...new Set(vendorSetsFromRows)];
    if (uniqueSets.length > 1) {
      vendorMode = "perItem";
      commonVendorIds = [];
    } else if (uniqueSets.length === 1 && !commonVendorIds.length) {
      commonVendorIds = normalizeVendorIds(
        rows.find((r) => normalizeVendorIds(r.vendor).length)?.vendor,
      );
      vendorMode = "common";
    }
  }

  if (vendorMode === "common") {
    rows.forEach((r) => {
      r.vendor = [];
    });
  }

  return {
    rfqDetails,
    rows,
    templateKind,
    vendorMode,
    commonVendorIds,
  };
}

/** Returns { ok, items, errors, total_amount } for Create RFQ payload. */
function buildRfqSubmitPayload(rows, { vendorMode, commonVendorIds } = {}) {
  const errors = [];
  const items = [];
  const normalizedCommonVendorIds = normalizeVendorIds(commonVendorIds);

  if (vendorMode === "common" && !normalizedCommonVendorIds.length) {
    errors.push("Please select at least one vendor for the RFQ.");
  }

  rows.forEach((r, i) => {
    if (rowIsEffectivelyEmpty(r)) return;
    const rowNo = i + 1;
    const item_description = String(r.itemDescription ?? "").trim();
    const qty = toNumberOrZero(r.qty);
    const uom = String(r.uom ?? "").trim();
    const unit_rate = toNumberOrZero(r.unitRate);
    const gst_rate = toNumberOrZero(r.gstRate);
    const remarks = String(r.remarks ?? "").trim();
    const rowVendorIds = normalizeVendorIds(r.vendor);

    if (!item_description) {
      errors.push(`Row ${rowNo}: Item description is required.`);
    }
    if (!String(r.qty ?? "").trim() || !Number.isFinite(qty) || qty <= 0) {
      errors.push(`Row ${rowNo}: Valid quantity is required.`);
    }
    if (!uom) {
      errors.push(`Row ${rowNo}: UoM is required.`);
    }
    if (!String(r.unitRate ?? "").trim() || !Number.isFinite(unit_rate) || unit_rate < 0) {
      errors.push(`Row ${rowNo}: Valid unit rate is required.`);
    }
    if (
      !String(r.gstRate ?? "").trim() ||
      !Number.isFinite(gst_rate) ||
      gst_rate < 0 ||
      gst_rate > 100
    ) {
      errors.push(`Row ${rowNo}: Valid GST rate (0–100) is required.`);
    }
    if (vendorMode === "perItem" && !rowVendorIds.length) {
      errors.push(`Row ${rowNo}: At least one vendor is required.`);
    }

    const amount = qty * unit_rate;
    items.push({
      item_description,
      quantity: qty,
      unit_of_measurement: uom,
      unit_rate,
      gst_rate,
      amount,
      remarks_from_buyer: remarks,
      vendor:
        vendorMode === "common"
          ? normalizedCommonVendorIds
          : rowVendorIds,
    });
  });

  if (!items.length && !errors.length) {
    errors.push(
      "Add at least one line item with description, quantity, UoM, unit rate, and GST rate.",
    );
  }

  const total_amount = items.reduce((s, it) => s + (it.amount || 0), 0);
  return { ok: errors.length === 0, items, errors, total_amount };
}

const GeneralProcurementEntry = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const { Panel } = Collapse;
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [templateKind, setTemplateKind] = useState("general");
  const [uploadErrors, setUploadErrors] = useState([]);
  const [createRfqLoading, setCreateRfqLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [vendorMode, setVendorMode] = useState("common");
  const [commonVendorIds, setCommonVendorIds] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [addVendorSubmitting, setAddVendorSubmitting] = useState(false);
  const [addVendorTarget, setAddVendorTarget] = useState({
    scope: "common",
    rowIndex: null,
  });
  const [addVendorForm] = Form.useForm();

  const loadVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const res = await getRequest(
        "general-procurement-vendor/active-vendors-by-company/",
      );
      const payload = res?.data;
      const list = Array.isArray(payload?.results?.data)
        ? payload.results.data
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
      const opts = list
        .map((v) => {
          const name =
            v?.vendor_registered_name ?? v?.vendor_name ?? v?.business_name ?? "";
          const value = v?.id ?? name;
          return value
            ? { value, label: name || String(value) }
            : null;
        })
        .filter(Boolean);
      const seen = new Set();
      const uniqueOpts = opts.filter((o) => {
        const k = String(o.value);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setVendorOptions(uniqueOpts);
      return uniqueOpts;
    } catch (e) {
      setVendorOptions([]);
      return [];
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const openAddVendorModal = (scope, rowIndex = null) => {
    setAddVendorTarget({ scope, rowIndex });
    setAddVendorOpen(true);
  };

  const closeAddVendorModal = () => {
    if (addVendorSubmitting) return;
    setAddVendorOpen(false);
  };

  useEffect(() => {
    if (addVendorOpen) {
      addVendorForm.resetFields();
    }
  }, [addVendorOpen, addVendorForm]);

  const submitVendorApi = async (payload) => {
    try {
      setAddVendorSubmitting(true);

      const formData = new FormData();
      formData.append("form_data", JSON.stringify(payload));
      formData.append("vendor_logo", "");

      const res = await postRequest("general-procurement-vendor/", formData);
      const data = res?.data;
      const succeeded =
        data?.success === true ||
        data?.success === "true" ||
        (data?.success !== false && (res?.status ?? 200) < 400);
      if (!succeeded) {
        ErrorNotificationMsg(
          (typeof data?.message === "string" && data.message) ||
            "Failed to add vendor.",
        );
        return;
      }

      const created =
        data?.results?.data ??
        data?.results ??
        data?.data ??
        data ??
        {};

      const newName =
        created?.name ??
        created?.vendor_name ??
        created?.business_name ??
        payload.name;
      const newValue = created?.id ?? newName;

      const refreshed = await loadVendors();
      const resolvedValue =
        newValue ??
        refreshed?.find(
          (o) =>
            String(o.label).trim().toLowerCase() ===
            String(payload.name).trim().toLowerCase(),
        )?.value;

      if (resolvedValue != null && resolvedValue !== "") {
        if (
          addVendorTarget.scope === "perItem" &&
          addVendorTarget.rowIndex != null
        ) {
          setRows((prev) =>
            prev.map((r, i) =>
              i === addVendorTarget.rowIndex
                ? { ...r, vendor: appendVendorId(r.vendor, resolvedValue) }
                : r,
            ),
          );
        } else {
          setCommonVendorIds((prev) => appendVendorId(prev, resolvedValue));
        }
      }

      SuccessNotificationMsg(
        (typeof data?.message === "string" && data.message) ||
          "Vendor added successfully.",
      );
      setAddVendorOpen(false);
      addVendorForm.resetFields();
    } catch (e) {
      const raw = e?.response?.data;
      const msg =
        raw?.message ||
        raw?.detail ||
        raw?.error ||
        (typeof raw === "object" &&
          raw &&
          Object.values(raw)?.[0]?.[0]) ||
        e?.message ||
        "Failed to add vendor.";
      ErrorNotificationMsg(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setAddVendorSubmitting(false);
    }
  };

  const handleAddVendorFormFinish = (values) => {
    const payload = {
      name: String(values.companyName || "").trim(),
      person_name: String(values.personName || "").trim(),
      email: String(values.email || "").trim(),
      phone: String(values.mobile || "").trim(),
    };

    Modal.confirm({
      title: "Confirm add vendor",
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <div>
            <strong>Company:</strong> {payload.name}
          </div>
          <div>
            <strong>Person:</strong> {payload.person_name}
          </div>
          <div>
            <strong>Mobile:</strong> {payload.phone}
          </div>
          <div>
            <strong>Email:</strong> {payload.email}
          </div>
        </div>
      ),
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { className: "SaveGradiantBtn" },
      onOk: () => submitVendorApi(payload),
    });
  };

  const [rfqDetails, setRfqDetails] = useState({
    currency: "INR",
    price_basis: "FIRM",
    freight: "Vendor Scope",
    transit_insurance: "Vendor Scope",
    unloading: "Vendor Scope",
    buyer_scope: "Buyer Scope",
    rfq_bid_due_date_time: moment().add(2, "days").set({ hour: 16, minute: 0 }),
    delivery_date: moment().add(7, "days"),
  });

  const setRfq = (patch) => setRfqDetails((p) => ({ ...p, ...patch }));

  useEffect(() => {
    if (!editId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setDetailLoading(true);
        const res = await getRequest(`general-rfqs/${editId}/`);
        const detail =
          res?.data?.results ??
          res?.data?.data ??
          res?.data;
        if (cancelled) return;
        if (!detail || typeof detail !== "object") {
          ErrorNotificationMsg("Could not load RFQ details.");
          navigate("/general-procurement-list");
          return;
        }
        const mapped = mapGeneralRfqApiToForm(detail);
        if (!mapped) {
          ErrorNotificationMsg("Could not load RFQ details.");
          navigate("/general-procurement-list");
          return;
        }
        setRfqDetails(mapped.rfqDetails);
        setRows(mapped.rows);
        setTemplateKind(mapped.templateKind);
        setVendorMode(mapped.vendorMode);
        setCommonVendorIds(mapped.commonVendorIds);
      } catch (e) {
        if (!cancelled) {
          ErrorNotificationMsg(
            e?.response?.data?.message ||
              e?.response?.data?.detail ||
              "Failed to load RFQ.",
          );
          navigate("/general-procurement-list");
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, navigate]);

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const addRowAfter = (index) => {
    setRows((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, { ...EMPTY_ROW });
      return next;
    });
  };

  const [deletingItemIds, setDeletingItemIds] = useState([]);

  const removeRow = (index) => {
    const target = rows[index];
    const itemId = target?.id;

    if (rows.length <= 1) {
      message.warning("At least one line item is required.");
      return;
    }

    if (!isEditMode || itemId == null || itemId === "") {
      setRows((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    Modal.confirm({
      title: "Delete this line item?",
      content:
        "This will permanently remove the item from the RFQ on the server.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeletingItemIds((prev) => [...prev, itemId]);
          await deleteRequest(`general-rfqs/${itemId}/`);
          setRows((prev) => prev.filter((r) => r.id !== itemId));
          SuccessNotificationMsg("Line item deleted.");
        } catch (e) {
          ErrorNotificationMsg(
            e?.response?.data?.message ||
              e?.response?.data?.detail ||
              e?.message ||
              "Failed to delete line item.",
          );
        } finally {
          setDeletingItemIds((prev) => prev.filter((id) => id !== itemId));
        }
      },
    });
  };

  const replaceRowsFromSheet = (parsedRows) => {
    const next =
      parsedRows.length > 0
        ? parsedRows.map((r) => ({ ...EMPTY_ROW, ...r }))
        : [{ ...EMPTY_ROW }];
    setRows(next);
  };

  const parseWorkbookToRows = (workbook) => {
    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName) {
      return { rows: [], errors: ["Uploaded file has no sheets."] };
    }
    const sheet = workbook.Sheets[firstSheetName];
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    if (!aoa.length) {
      return { rows: [], errors: ["Uploaded sheet is empty."] };
    }

    const normalizeHeader = (s) =>
      String(s ?? "")
        .toLowerCase()
        .replace(/\(.*?\)/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const HEADER_ALIASES = {
      itemDescription: ["item description", "description", "item"],
      qty: ["qty", "quantity", "qty no", "qty nos"],
      uom: ["uom", "unit of measurement", "unit", "units"],
      unitRate: ["unit rate", "rate", "price", "unit price"],
      gstRate: ["gst rate", "gst", "gst %", "gst percent", "tax rate"],
      remarks: [
        "remarks from buyer",
        "remarks",
        "buyer remarks",
        "remark",
        "notes",
      ],
    };

    const headerRow = (aoa[0] || []).map((c) => normalizeHeader(c));
    const columnIndex = {};
    Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
      const idx = headerRow.findIndex((h) => aliases.includes(h));
      if (idx !== -1) columnIndex[key] = idx;
    });

    const missing = [
      "itemDescription",
      "qty",
      "uom",
      "unitRate",
      "gstRate",
    ].filter((k) => columnIndex[k] == null);
    if (missing.length) {
      const labelByKey = {
        itemDescription: "Item Description",
        qty: "Qty",
        uom: "UoM",
        unitRate: "Unit Rate",
        gstRate: "GST Rate",
        remarks: "Remarks from Buyer (if any)",
      };
      return {
        rows: [],
        errors: [
          `Could not detect required columns: ${missing
            .map((k) => labelByKey[k])
            .join(", ")}. Please use the latest downloaded template (Item Description | Qty | UoM | Unit Rate | GST Rate | Remarks from Buyer (if any)).`,
        ],
      };
    }

    const getCell = (row, key) => {
      const i = columnIndex[key];
      return i == null ? "" : row[i];
    };

    const errors = [];
    const rowsOut = [];
    for (let i = 1; i < aoa.length; i += 1) {
      const row = aoa[i] || [];
      const itemDescription = String(getCell(row, "itemDescription") ?? "").trim();
      const qtyRaw = getCell(row, "qty");
      const uomRaw = getCell(row, "uom");
      const unitRateRaw = getCell(row, "unitRate");
      const gstRateRaw = getCell(row, "gstRate");
      const remarks = String(getCell(row, "remarks") ?? "").trim();

      const isTotallyEmpty =
        !itemDescription &&
        (qtyRaw === "" || qtyRaw == null) &&
        (uomRaw === "" || uomRaw == null) &&
        (unitRateRaw === "" || unitRateRaw == null) &&
        (gstRateRaw === "" || gstRateRaw == null) &&
        !remarks;
      if (isTotallyEmpty) continue;

      const rowLabel = `Row ${i + 1}`;
      if (!itemDescription) errors.push(`${rowLabel}: Item Description is required.`);

      const qtyStr =
        qtyRaw === "" || qtyRaw == null ? "" : String(qtyRaw).trim();
      const unitRateStr =
        unitRateRaw === "" || unitRateRaw == null
          ? ""
          : String(unitRateRaw).trim();

      const qty = limitToDigits(qtyStr, 6, true);
      const unitRate = limitToDigits(unitRateStr, 6, true);
      const gstRateStr =
        gstRateRaw === "" || gstRateRaw == null
          ? ""
          : String(gstRateRaw).trim();
      const gstRate = limitToDigits(gstRateStr, 5, true);

      if (!qty) errors.push(`${rowLabel}: Qty is required.`);
      if (!unitRate) errors.push(`${rowLabel}: Unit Rate is required.`);
      if (gstRateStr === "" || gstRate == null) {
        errors.push(`${rowLabel}: GST Rate is required.`);
      } else if (toNumberOrZero(gstRate) > 100) {
        errors.push(`${rowLabel}: GST Rate must be between 0 and 100.`);
      }

      const uom = String(uomRaw ?? "").trim();
      if (!uom) errors.push(`${rowLabel}: UoM is required.`);

      rowsOut.push({
        itemDescription,
        qty,
        uom,
        unitRate,
        gstRate,
        remarks,
      });
    }

    if (!rowsOut.length && !errors.length) {
      errors.push("No data rows found after the header.");
    }

    return { rows: rowsOut, errors };
  };

  const uploadProps = {
    accept: ".xlsx,.xls",
    showUploadList: false,
    beforeUpload: (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const { rows: parsed, errors } = parseWorkbookToRows(wb);
          setUploadErrors(errors);
          if (errors.length && !parsed.length) {
            message.error(
              "Could not import the sheet. Please review the alerts above the table.",
            );
            return;
          }
          if (errors.length) {
            message.warning(
              "Uploaded sheet has issues. Please review the alerts above the table.",
            );
            replaceRowsFromSheet(parsed);
            return;
          }
          replaceRowsFromSheet(parsed);
          message.success("Sheet imported successfully. You can edit rows below.");
        } catch (err) {
          setUploadErrors([err?.message || "Failed to read the uploaded file."]);
          message.error("Failed to read the uploaded file.");
        }
      };
      reader.readAsArrayBuffer(file);
      return false;
    },
  };

  const dataSource = useMemo(
    () =>
      rows.map((r, index) => {
        const qty = toNumberOrZero(r.qty);
        const unitRate = toNumberOrZero(r.unitRate);
        const amount = qty * unitRate;
        return {
          key: String(index),
          index,
          ...r,
          amount,
        };
      }),
    [rows]
  );

  const grandTotal = useMemo(
    () => dataSource.reduce((sum, r) => sum + (r.amount || 0), 0),
    [dataSource]
  );

  const vendorSelectionMissing = useMemo(() => {
    if (vendorMode === "common") {
      return !normalizeVendorIds(commonVendorIds).length;
    }
    const filledRows = rows.filter((r) => !rowIsEffectivelyEmpty(r));
    if (!filledRows.length) return true;
    return filledRows.some(
      (r) => !normalizeVendorIds(r.vendor).length,
    );
  }, [vendorMode, commonVendorIds, rows]);

  const vendorMissingMessage =
    vendorMode === "common"
      ? "Please select at least one vendor for the RFQ before submitting."
      : "Please select at least one vendor for every line item before submitting.";

  const columns = useMemo(
    () => [
      {
        title: "Sr.",
        dataIndex: "index",
        width: 80,
        render: (_, record) => record.index + 1,
      },
      {
        title: "Item Description",
        dataIndex: "itemDescription",
        render: (_, record) => (
          <Input
            value={record.itemDescription}
            placeholder="Enter item description"
            onChange={(e) =>
              updateRow(record.index, { itemDescription: e.target.value })
            }
          />
        ),
      },
      {
        title: "Qty.",
        dataIndex: "qty",
        width: 120,
        render: (_, record) => (
          <Input
            value={record.qty}
            inputMode="decimal"
            maxLength={6}
            placeholder="0"
            onChange={(e) => {
              const value = limitToDigits(e.target.value, 6, true);
              updateRow(record.index, { qty: value });
            }}
          />
        ),
      },
      {
        title: "UoM.",
        dataIndex: "uom",
        width: 140,
        render: (_, record) => (
          <Input
            value={record.uom}
            placeholder="e.g. Nos, Kg, Meter"
            onChange={(e) =>
              updateRow(record.index, { uom: e.target.value })
            }
          />
        ),
      },
      {
        title: "Unit Rate",
        dataIndex: "unitRate",
        width: 140,
        render: (_, record) => (
          <Input
            value={record.unitRate}
            inputMode="decimal"
            maxLength={6}
            placeholder="0"
            onChange={(e) => {
              const value = limitToDigits(e.target.value, 6, true);
              updateRow(record.index, { unitRate: value });
            }}
          />
        ),
      },
      {
        title: "GST Rate",
        dataIndex: "gstRate",
        width: 120,
        render: (_, record) => (
          <Input
            value={record.gstRate}
            inputMode="decimal"
            maxLength={5}
            placeholder="0"
            suffix="%"
            onChange={(e) => {
              const value = limitToDigits(e.target.value, 5, true);
              updateRow(record.index, { gstRate: value });
            }}
          />
        ),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        width: 140,
        align: "right",
        render: (_, record) => (
          <Input
            style={{ textAlign: "right" }}
            value={Number(record.amount || 0).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
            readOnly
          />
        ),
      },
      {
        title: "Remarks from Buyer (if any)",
        dataIndex: "remarks",
        width: 220,
        render: (_, record) => (
          <Input
            value={record.remarks}
            placeholder="Enter remarks"
            onChange={(e) => updateRow(record.index, { remarks: e.target.value })}
          />
        ),
      },
      ...(vendorMode === "perItem"
        ? [
            {
              title: "Vendor",
              dataIndex: "vendor",
              width: 260,
              render: (_, record) => (
                <div style={{ display: "flex", gap: 6 }}>
                  <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    placeholder="Select vendor(s)"
                    loading={vendorsLoading}
                    style={{ flex: 1, minWidth: 0 }}
                    value={normalizeVendorIds(record.vendor)}
                    options={vendorOptions}
                    onChange={(v) =>
                      updateRow(record.index, { vendor: v ?? [] })
                    }
                  />
                  {/* <Tooltip title="Add new vendor">
                    <Button
                      icon={<UserAddOutlined />}
                      onClick={() =>
                        openAddVendorModal("perItem", record.index)
                      }
                    />
                  </Tooltip> */}
                </div>
              ),
            },
          ]
        : []),
      {
        title: "",
        key: "actions",
        width: 90,
        fixed: "right",
        render: (_, record) => (
          <div style={{ display: "flex", gap: 10 }}>
            <Tooltip title="Add row">
              <Button
                type="text"
                aria-label="Add row"
                icon={<PlusSquareFilled />}
                onClick={() => addRowAfter(record.index)}
              />
            </Tooltip>
            <Tooltip title="Delete row">
              <Button
                type="text"
                danger
                disabled={
                  rows.length <= 1 ||
                  (record?.id != null &&
                    deletingItemIds.includes(record.id))
                }
                loading={
                  record?.id != null &&
                  deletingItemIds.includes(record.id)
                }
                aria-label="Delete row"
                icon={<MinusSquareFilled />}
                onClick={() => removeRow(record.index)}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    [
      rows.length,
      vendorMode,
      vendorOptions,
      vendorsLoading,
      deletingItemIds,
    ]
  );

  const handleCreateRfq = async () => {
    const { ok, items, errors, total_amount } = buildRfqSubmitPayload(rows, {
      vendorMode,
      commonVendorIds,
    });
    if (!ok) {
      message.warning(errors[0] || "Please fix the line items.");
      return;
    }

    const payload = {
      currency: rfqDetails.currency,
      price_basis: rfqDetails.price_basis,
      freight: rfqDetails.freight,
      transit_insurance: rfqDetails.transit_insurance,
      unloading: rfqDetails.unloading,
      buyer_scope: rfqDetails.buyer_scope,
      open_order: false,
      rfq_bid_due_date_time: rfqDetails.rfq_bid_due_date_time
        ? moment(rfqDetails.rfq_bid_due_date_time).format("YYYY-MM-DD HH:mm")
        : null,
      delivery_date: rfqDetails.delivery_date
        ? moment(rfqDetails.delivery_date).format("YYYY-MM-DD")
        : null,
      rfq_type: templateKind,
      vendor_mode: vendorMode,
      vendor:
        vendorMode === "common" ? normalizeVendorIds(commonVendorIds) : null,
      items,
      total_amount,
    };

    try {
      setCreateRfqLoading(true);
      const response = editId
        ? await patchRequest(`general-rfqs/${editId}/`, payload)
        : await postRequest(GENERAL_PROCUREMENT_RFQ_CREATE_URL, payload);

      if (response?.data?.success === false) {
        const rawFail =
          response?.data?.message ??
          response?.data?.error ??
          response?.data?.detail;
        ErrorNotificationMsg(
          formatNotificationMessage(
            rawFail,
            editId ? "Failed to update RFQ." : "Failed to create RFQ.",
          ),
        );
        return;
      }

      SuccessNotificationMsg(
        formatNotificationMessage(
          response?.data?.message,
          editId ? "RFQ updated successfully." : "RFQ created successfully.",
        ),
      );
      navigate("/general-procurement-list");
    } catch (e) {
      const data = e?.response?.data;
      let raw =
        data?.message ?? data?.error ?? data?.detail ?? null;
      if (raw == null && data && typeof data === "object" && !Array.isArray(data)) {
        raw = data;
      }
      if (raw == null) raw = e?.message;
      ErrorNotificationMsg(
        formatNotificationMessage(
          raw,
          editId ? "Failed to update RFQ." : "Failed to create RFQ.",
        ),
      );
    } finally {
      setCreateRfqLoading(false);
    }
  };

  return (
    <>
    <div className="ListingTopBar">
            <div className="pagenamewrap">
              <div className="pagename">
                <h3>
                  {isEditMode ? "Edit Quick / General RFQ" : "Quick / General RFQ"}
                </h3>
    
                <Breadcrumb className="Breadcrumb-Sec">
                  <Breadcrumb.Item>
                    <Link to="/purchase-requisition-history">{t("home")}</Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    <Link to="/general-procurement-list">{t("general-procurement-list")}</Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{isEditMode ? "Edit Quick RFQ" : "Quick RFQ"}</Breadcrumb.Item>
                            
                </Breadcrumb>
              </div>
             
            </div>
          </div>
    <div className="stepscontentwrap">
        <div className="stepsContent">
          <section className="grid-sec ">
        <Spin spinning={detailLoading}>
       <div className="PR-body" style={{ padding: "0" }}>
          <Collapse
            defaultActiveKey={["1"]}
            className="collapsescrl"
            
          >
            <Panel header="RFQ Details" key="1">
              <Row gutter={[16, 12]} className="customcol">
                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    <span style={{ color: "red" }}>*</span> Currency
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={rfqDetails.currency}
                    options={CURRENCY_OPTIONS}
                    onChange={(v) => setRfq({ currency: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    <span style={{ color: "red" }}>*</span> Price Basis
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={rfqDetails.price_basis}
                    options={PRICE_BASIS_OPTIONS}
                    onChange={(v) => setRfq({ price_basis: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    <span style={{ color: "red" }}>*</span> Freight
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={rfqDetails.freight}
                    options={SCOPE_OPTIONS}
                    onChange={(v) => setRfq({ freight: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    Transit Insurance
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={rfqDetails.transit_insurance}
                    options={SCOPE_OPTIONS}
                    onChange={(v) => setRfq({ transit_insurance: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    <span style={{ color: "red" }}>*</span> Unloading
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={rfqDetails.unloading}
                    options={SCOPE_OPTIONS}
                    onChange={(v) => setRfq({ unloading: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    Buyer Scope
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={rfqDetails.buyer_scope}
                    options={SCOPE_OPTIONS}
                    onChange={(v) => setRfq({ buyer_scope: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    <span style={{ color: "red" }}>*</span> RFQ BID Due Date &amp; Time
                  </label>
                  <DatePicker
                    style={{ width: "100%" }}
                    showTime={{ format: "HH:mm" }}
                    format="YYYY-MM-DD HH:mm"
                    value={rfqDetails.rfq_bid_due_date_time}
                    onChange={(v) => setRfq({ rfq_bid_due_date_time: v })}
                  />
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  xl={8}
                  xxl={6}
                  className="TextFeild gutter-row"
                >
                  <label className="cp" style={{ paddingTop: 10, display: "block" }}>
                    <span style={{ color: "red" }}>*</span> Delivery Date
                  </label>
                  <DatePicker
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    value={rfqDetails.delivery_date}
                    onChange={(v) => setRfq({ delivery_date: v })}
                  />
                </Col>
              </Row>
            </Panel>
          </Collapse>

          <Card style={{ marginBottom: 16, borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "nowrap",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                <Button
                  className="SaveGradiantBtn"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadTemplate(templateKind)}
                >
                  Download template
                </Button>
                <Upload {...uploadProps}>
                  <Button className="SaveGradiantBtn" icon={<UploadOutlined />}>
                    Upload filled sheet
                  </Button>
                </Upload>
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: "#6B7280",
                  fontSize: 13,
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title="Download the template, fill it, then upload. The table below will refresh with your sheet data so you can correct values before submitting."
              >
                Download the template, fill it, then upload. The table below will refresh
                with your sheet data so you can correct values before submitting.
              </div>
            </div>

            {uploadErrors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Alert
                  type="warning"
                  showIcon
                  message="Please fix the following issues (rows are still loaded for editing where possible)"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {uploadErrors.map((e, idx) => (
                        <li key={idx}>{e}</li>
                      ))}
                    </ul>
                  }
                />
              </div>
            )}
          </Card>

          <Card
            style={{ marginBottom: 16, borderRadius: 12 }}
            bodyStyle={{ padding: 16 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontWeight: 600, color: "#111827", fontSize: 15 }}>
                Vendor selection
              </div>

              <Radio.Group
                value={vendorMode}
                onChange={(e) => {
                  const next = e.target.value;
                  setVendorMode(next);
                  if (next === "common") {
                    setRows((prev) =>
                      prev.map((r) => ({ ...r, vendor: [] })),
                    );
                  } else {
                    setCommonVendorIds([]);
                  }
                }}
              >
                <Radio value="common">Same vendor for all items</Radio>
                <Radio value="perItem">Different vendor per item</Radio>
              </Radio.Group>

              {vendorMode === "common" ? (
                <div
                  style={{
                    minWidth: 260,
                    flex: "0 1 420px",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    placeholder="Select vendor(s)"
                    loading={vendorsLoading}
                    style={{ flex: 1, minWidth: 0 }}
                    value={normalizeVendorIds(commonVendorIds)}
                    options={vendorOptions}
                    onChange={(v) => setCommonVendorIds(v ?? [])}
                  />
                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    icon={<UserAddOutlined />}
                    onClick={() => openAddVendorModal("common")}
                  >
                    Add Vendor
                  </Button>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ color: "#6B7280", fontSize: 13 }}>
                    Pick one or more vendors for each row in the table below.
                  </span>
                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    icon={<UserAddOutlined />}
                    onClick={() => openAddVendorModal("common")}
                  >
                    Add Vendor
                  </Button>
                </div>
              )}
            </div>
          </Card>

      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: 1200 }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1} colSpan={4} style={{ textAlign: "right" }}>
                <div
                  className="totalCityOverallSitee5"
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    letterSpacing: "1px",
                  }}
                >
                  Total
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell
                index={5}
                align="right"
              >
                <div className="totalCityOverallSitee5" style={{ fontWeight: "bold" }}>
                  ₹{" "}
                  {grandTotal.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} />
              {vendorMode === "perItem" && (
                <Table.Summary.Cell index={7} />
              )}
              <Table.Summary.Cell
                index={vendorMode === "perItem" ? 8 : 7}
              />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Tooltip
              title={vendorSelectionMissing ? vendorMissingMessage : ""}
              color="#094E8F"
            >
              <Button
                type="primary"
                className="SaveGradiantBtn"
                loading={createRfqLoading}
                disabled={detailLoading || vendorSelectionMissing}
                onClick={handleCreateRfq}
              >
                {isEditMode ? "Update RFQ" : "Create RFQ"}
              </Button>
            </Tooltip>
          </div>
        </div>
        </Spin>
        </section>
      </div>
      </div>

      <Modal
        title="Add Vendor"
        open={addVendorOpen}
        onCancel={closeAddVendorModal}
        footer={null}
        maskClosable={!addVendorSubmitting}
        destroyOnClose
        width={640}
      >
        <Form
          form={addVendorForm}
          layout="vertical"
          autoComplete="off"
          onFinish={handleAddVendorFormFinish}
          preserve={false}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[
                  { required: true, message: "Company name is required." },
                  {
                    whitespace: true,
                    message: "Company name cannot be blank.",
                  },
                  { max: 200, message: "Max 200 characters." },
                ]}
              >
                <Input
                  placeholder="Enter company name"
                  maxLength={200}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="personName"
                label="Person Name"
                rules={[
                  { required: true, message: "Person name is required." },
                  {
                    whitespace: true,
                    message: "Person name cannot be blank.",
                  },
                  { max: 100, message: "Max 100 characters." },
                ]}
              >
                <Input
                  placeholder="Enter contact person name"
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="mobile"
                label="Mob No."
                rules={[
                  { required: true, message: "Mobile number is required." },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit mobile number.",
                  },
                ]}
              >
                <Input
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                    if (
                      !/^[0-9]$/.test(e.key) &&
                      ![
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email Id"
                rules={[
                  { required: true, message: "Email is required." },
                  { type: "email", message: "Enter a valid email address." },
                  { max: 100, message: "Max 100 characters." },
                ]}
              >
                <Input
                  placeholder="name@example.com"
                  maxLength={100}
                />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 8,
            }}
          >
            <Button
              onClick={closeAddVendorModal}
              disabled={addVendorSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="SaveGradiantBtn"
              loading={addVendorSubmitting}
            >
              Submit
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default GeneralProcurementEntry;